import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { customersAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import useDebounce from '../../hooks/useDebounce';
import {
  Search, RefreshCw, Users, Download, Plus,
  Star, TrendingUp, AlertCircle, ChevronLeft, ChevronRight,
  Tag, Eye, Trash2
} from 'lucide-react';
import '../Admin/AdminDashboard.css';
import './AdminLeads.css';

const SEGMENT_META = {
  new:        { label: 'New',        color: '#3b82f6', bg: '#3b82f620' },
  occasional: { label: 'Occasional', color: '#f59e0b', bg: '#f59e0b20' },
  repeat:     { label: 'Repeat',     color: '#8b5cf6', bg: '#8b5cf620' },
  vip:        { label: 'VIP',        color: '#f97316', bg: '#f97316 20' },
  'at-risk':  { label: 'At Risk',    color: '#ef4444', bg: '#ef444420' },
};

const AdminCustomers = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const segmentFilter = searchParams.get('segment') || '';
  const [localSearch, setLocalSearch] = useState(searchParams.get('search') || '');
  const debouncedSearch = useDebounce(localSearch, 300);

  const setPage = (p) => {
    const np = new URLSearchParams(searchParams);
    np.set('page', p);
    setSearchParams(np, { replace: true });
  };

  const updateFilter = (key, value) => {
    const np = new URLSearchParams(searchParams);
    value ? np.set(key, value) : np.delete(key);
    np.delete('page');
    setSearchParams(np, { replace: true });
  };

  useEffect(() => {
    const np = new URLSearchParams(searchParams);
    debouncedSearch ? np.set('search', debouncedSearch) : np.delete('search');
    np.delete('page');
    setSearchParams(np, { replace: true });
  }, [debouncedSearch]); // eslint-disable-line

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 20,
        search: searchParams.get('search') || undefined,
        segment: segmentFilter || undefined,
      };
      const res = await customersAPI.getAll(params);
      setCustomers(res.data.customers || []);
      setTotalPages(res.data.pagination?.totalPages || 1);
      setTotalCount(res.data.pagination?.total || 0);
    } catch {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchParams, segmentFilter]); // eslint-disable-line

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete customer "${name}"? This cannot be undone.`)) return;
    try {
      await customersAPI.delete(id);
      toast.success('Customer deleted');
      loadCustomers();
    } catch {
      toast.error('Failed to delete customer');
    }
  };

  const handleExport = async () => {
    try {
      const res = await customersAPI.exportCsv();
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  const formatRevenue = (n) => `€${(n || 0).toFixed(0)}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content">
        <div className="content-header">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Users size={28} color="#8b5cf6" /> Customers
            </h1>
            <p style={{ color: '#a1a1aa' }}>{totalCount} total customers in your CRM</p>
          </div>
          <div className="admin-header-right">
            <button className="btn-secondary" onClick={handleExport} title="Export CSV">
              <Download size={16} /> Export
            </button>
            <button className="btn-secondary" onClick={loadCustomers}>
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 240px' }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#71717a' }} />
            <input
              type="text"
              placeholder="Search by name, email, company…"
              value={localSearch}
              onChange={e => setLocalSearch(e.target.value)}
              style={{
                width: '100%', paddingLeft: 38, paddingRight: 12,
                paddingTop: 10, paddingBottom: 10,
                background: '#18181b', border: '1px solid #27272a',
                borderRadius: 8, color: '#fff', fontSize: 14, boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Segment filter chips */}
          <div style={{ display: 'flex', gap: 8 }}>
            {['', 'new', 'occasional', 'repeat', 'vip', 'at-risk'].map(s => (
              <button
                key={s}
                onClick={() => updateFilter('segment', s)}
                style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                  border: segmentFilter === s ? '2px solid #8b5cf6' : '1px solid #27272a',
                  background: segmentFilter === s ? '#8b5cf620' : '#18181b',
                  color: segmentFilter === s ? '#a78bfa' : '#a1a1aa',
                  fontWeight: segmentFilter === s ? 600 : 400,
                }}
              >
                {s ? (SEGMENT_META[s]?.label || s) : 'All Segments'}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div style={{ background: '#111', borderRadius: 12, overflow: 'hidden', border: '1px solid #27272a' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#18181b', borderBottom: '1px solid #27272a' }}>
                {['Customer', 'Segment', 'Orders', 'Revenue', 'Last Activity', 'Tags', ''].map(h => (
                  <th key={h} style={{ padding: '12px 16px', color: '#71717a', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#71717a' }}>
                  <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
                </td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#71717a' }}>
                  No customers found. They are created automatically when leads are processed.
                </td></tr>
              ) : customers.map(c => {
                const seg = SEGMENT_META[c.segment] || SEGMENT_META.new;
                return (
                  <tr
                    key={c._id}
                    onClick={() => navigate(`/admin/customers/${c._id}`)}
                    style={{ borderBottom: '1px solid #1c1c1c', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#18181b'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 600, color: '#fff' }}>{c.name || c.email}</div>
                      <div style={{ color: '#71717a', fontSize: 12 }}>{c.email}</div>
                      {c.company && <div style={{ color: '#52525b', fontSize: 12 }}>{c.company}</div>}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 12,
                        background: seg.bg, color: seg.color,
                        fontSize: 12, fontWeight: 600
                      }}>{seg.label}</span>
                    </td>
                    <td style={{ padding: '14px 16px', color: '#fff', fontWeight: 600 }}>
                      {c.lifetimeStats?.orderCount || 0}
                    </td>
                    <td style={{ padding: '14px 16px', color: '#10b981', fontWeight: 600 }}>
                      {formatRevenue(c.lifetimeStats?.revenue)}
                    </td>
                    <td style={{ padding: '14px 16px', color: '#71717a', fontSize: 13 }}>
                      {formatDate(c.lastInteractionAt || c.createdAt)}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(c.tags || []).slice(0, 3).map(tag => (
                          <span key={tag} style={{
                            padding: '2px 8px', borderRadius: 10, fontSize: 11,
                            background: '#27272a', color: '#a1a1aa'
                          }}>
                            <Tag size={9} style={{ marginRight: 3 }} />{tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button
                          title="View 360°"
                          onClick={() => navigate(`/admin/customers/${c._id}`)}
                          style={{ background: '#27272a', border: 'none', borderRadius: 6, padding: '6px 10px', color: '#a78bfa', cursor: 'pointer' }}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          title="Delete"
                          onClick={() => handleDelete(c._id, c.name || c.email)}
                          style={{ background: '#27272a', border: 'none', borderRadius: 6, padding: '6px 10px', color: '#ef4444', cursor: 'pointer' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
            <button
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
              style={{ padding: '8px 16px', borderRadius: 6, background: '#18181b', border: '1px solid #27272a', color: currentPage <= 1 ? '#52525b' : '#fff', cursor: currentPage <= 1 ? 'default' : 'pointer' }}
            >
              <ChevronLeft size={16} />
            </button>
            <span style={{ padding: '8px 16px', color: '#a1a1aa', fontSize: 14 }}>
              {currentPage} / {totalPages}
            </span>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setPage(currentPage + 1)}
              style={{ padding: '8px 16px', borderRadius: 6, background: '#18181b', border: '1px solid #27272a', color: currentPage >= totalPages ? '#52525b' : '#fff', cursor: currentPage >= totalPages ? 'default' : 'pointer' }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminCustomers;
